import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Divider,
  Alert,
  Fade,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
} from '@mui/material';
import {
  AccessTime,
  Person,
  CheckCircle,
  Schedule,
  LocationOn,
  ArrowBack,
  WbSunny,
  LightMode,
  NightsStay,
  CalendarMonth,
  EventAvailable,
  Info,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

// Service interface
interface Service {
  id: string;
  name: string;
  description: string;
  duration: number;
  image: string;
  newPatient: boolean;
  existingPatient: boolean;
}

// Time slot interface
interface TimeSlot {
  time: string;
  period: 'morning' | 'afternoon' | 'evening';
  available: boolean;
}

// Form data interface
interface BookingFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  appointmentReason: string;
  emailReminder: boolean;
  smsReminder: boolean;
}

// Services - different for new vs existing patients
const allServices: Service[] = [
  {
    id: 'initial-consultation',
    name: 'Initial Consultation',
    description: 'Comprehensive first visit including health history, examination, and treatment plan discussion.',
    duration: 60,
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop',
    newPatient: true,
    existingPatient: false,
  },
  {
    id: 'new-patient-assessment',
    name: 'New Patient Assessment',
    description: 'Detailed spinal and postural assessment for first-time visitors.',
    duration: 45,
    image: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400&h=250&fit=crop',
    newPatient: true,
    existingPatient: false,
  },
  {
    id: 'standard-adjustment',
    name: 'Standard Adjustment',
    description: 'Regular chiropractic adjustment and spinal manipulation therapy.',
    duration: 30,
    image: 'https://images.unsplash.com/photo-1519824145371-296894a0daa9?w=400&h=250&fit=crop',
    newPatient: false,
    existingPatient: true,
  },
  {
    id: 'followup-consultation',
    name: 'Follow-up Consultation',
    description: 'Review your progress and adjust your treatment plan as needed.',
    duration: 30,
    image: 'https://images.unsplash.com/photo-1666214280557-f1b5022eb634?w=400&h=250&fit=crop',
    newPatient: false,
    existingPatient: true,
  },
  {
    id: 'sports-therapy',
    name: 'Sports Therapy',
    description: 'Specialized treatment for sports injuries and athletic performance.',
    duration: 45,
    image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=250&fit=crop',
    newPatient: true,
    existingPatient: true,
  },
  {
    id: 'remedial-massage',
    name: 'Remedial Massage',
    description: 'Therapeutic massage to relieve muscle tension and pain.',
    duration: 45,
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=400&h=250&fit=crop',
    newPatient: false,
    existingPatient: true,
  },
  {
    id: 'pregnancy-care',
    name: 'Pregnancy Care',
    description: 'Gentle chiropractic care tailored for expecting mothers.',
    duration: 30,
    image: 'https://images.unsplash.com/photo-1493894473891-10fc1e5dbd22?w=400&h=250&fit=crop',
    newPatient: true,
    existingPatient: true,
  },
  {
    id: 'pediatric-care',
    name: 'Pediatric Care',
    description: 'Safe and gentle chiropractic care for children of all ages.',
    duration: 30,
    image: 'https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=400&h=250&fit=crop',
    newPatient: true,
    existingPatient: true,
  },
];

// Generate time slots for a given date
const generateTimeSlots = (date: Date): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  const dayOfWeek = date.getDay();

  // Closed on Sundays
  if (dayOfWeek === 0) return [];

  // Saturday: 8am - 12pm, Weekdays: 8am - 6pm
  const isSaturday = dayOfWeek === 6;

  // Morning slots (8:00 - 11:30)
  for (let hour = 8; hour < 12; hour++) {
    for (let min = 0; min < 60; min += 30) {
      if (hour === 11 && min === 30) continue;
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      slots.push({
        time,
        period: 'morning',
        available: Math.random() > 0.3,
      });
    }
  }

  if (!isSaturday) {
    // Afternoon slots (12:00 - 16:30)
    for (let hour = 12; hour < 17; hour++) {
      for (let min = 0; min < 60; min += 30) {
        if (hour === 16 && min === 30) continue;
        const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        slots.push({
          time,
          period: 'afternoon',
          available: Math.random() > 0.35,
        });
      }
    }

    // Evening slots (17:00 - 18:00)
    for (let hour = 17; hour < 18; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        slots.push({
          time,
          period: 'evening',
          available: Math.random() > 0.4,
        });
      }
    }
  }

  return slots;
};

// Get available dates (next 14 days, excluding Sundays)
const getAvailableDates = (): Date[] => {
  const dates: Date[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 0; i < 21; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() + i);
    if (date.getDay() !== 0) { // Exclude Sundays
      dates.push(date);
    }
    if (dates.length >= 14) break;
  }

  return dates;
};

// Format time to 12-hour format
const formatTime12 = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'pm' : 'am';
  const hour12 = hours % 12 || 12;
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Main Booking System Component
const BookingSystem: React.FC = () => {
  const [step, setStep] = useState(0);
  const [patientType, setPatientType] = useState<'new' | 'existing' | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [formData, setFormData] = useState<BookingFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    appointmentReason: '',
    emailReminder: true,
    smsReminder: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [confirmationNumber, setConfirmationNumber] = useState('');

  const availableDates = useMemo(() => getAvailableDates(), []);

  // Get services based on patient type
  const getServices = () => {
    if (!patientType) return [];
    return allServices.filter(s =>
      patientType === 'new' ? s.newPatient : s.existingPatient
    );
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setTimeSlots(generateTimeSlots(date));
  };

  // Handle form change
  const handleFormChange = (field: keyof BookingFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Check if can proceed to next step
  const canProceed = (): boolean => {
    switch (step) {
      case 0: return patientType !== null;
      case 1: return selectedService !== null;
      case 2: return selectedDate !== null && selectedTime !== null;
      case 3: return formData.firstName !== '' && formData.lastName !== '' &&
                     formData.email !== '' && formData.phone !== '';
      default: return false;
    }
  };

  // Handle next step
  const handleNext = () => {
    if (step === 3) {
      // Submit booking
      setIsSubmitting(true);
      setTimeout(() => {
        setIsSubmitting(false);
        setConfirmationNumber(`HBC${Date.now().toString(36).toUpperCase()}`);
        setBookingComplete(true);
        setStep(4);
      }, 1500);
    } else {
      setStep(prev => prev + 1);
    }
  };

  // Handle back
  const handleBack = () => {
    if (step === 1) {
      setSelectedService(null);
    } else if (step === 2) {
      setSelectedDate(null);
      setSelectedTime(null);
    }
    setStep(prev => prev - 1);
  };

  // Reset booking
  const resetBooking = () => {
    setStep(0);
    setPatientType(null);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setBookingComplete(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      appointmentReason: '',
      emailReminder: true,
      smsReminder: true,
    });
  };

  // Group time slots by period
  const groupedSlots = useMemo(() => {
    const morning = timeSlots.filter(s => s.period === 'morning' && s.available);
    const afternoon = timeSlots.filter(s => s.period === 'afternoon' && s.available);
    const evening = timeSlots.filter(s => s.period === 'evening' && s.available);
    return { morning, afternoon, evening };
  }, [timeSlots]);

  // Step labels
  const steps = ['Patient Type', 'Select Service', 'Choose Time', 'Your Details'];

  // Render patient type selection (Step 0)
  const renderPatientType = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#2c3e50' }}>
          Welcome to Online Booking
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Are you a new or existing patient?
        </Typography>

        <Grid container spacing={3}>
          {[
            { type: 'new' as const, title: 'New Patient', desc: "I haven't visited before" },
            { type: 'existing' as const, title: 'Existing Patient', desc: "I've visited this practice before" },
          ].map(({ type, title, desc }) => (
            <Grid item xs={12} sm={6} key={type}>
              <MotionPaper
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setPatientType(type)}
                elevation={0}
                sx={{
                  p: 4,
                  cursor: 'pointer',
                  borderRadius: 3,
                  border: patientType === type
                    ? '2px solid #8bc34a'
                    : '2px solid #e0e0e0',
                  backgroundColor: patientType === type
                    ? 'rgba(139, 195, 74, 0.08)'
                    : 'white',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  '&:hover': {
                    borderColor: '#8bc34a',
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    mx: 'auto',
                    mb: 2,
                    backgroundColor: patientType === type ? '#8bc34a' : '#e0e0e0',
                  }}
                >
                  <Person sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {desc}
                </Typography>
                {patientType === type && (
                  <CheckCircle
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      color: '#8bc34a',
                    }}
                  />
                )}
              </MotionPaper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Fade>
  );

  // Render service selection (Step 1)
  const renderServiceSelection = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#2c3e50' }}>
          Select a Service
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Choose the type of appointment you'd like to book
        </Typography>

        <Grid container spacing={3}>
          {getServices().map((service) => (
            <Grid item xs={12} sm={6} md={4} key={service.id}>
              <MotionPaper
                whileHover={{ y: -4 }}
                onClick={() => setSelectedService(service)}
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: selectedService?.id === service.id
                    ? '2px solid #8bc34a'
                    : '2px solid transparent',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    height: 140,
                    backgroundImage: `url(${service.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'relative',
                  }}
                >
                  {selectedService?.id === service.id && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        backgroundColor: '#8bc34a',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  )}
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {service.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mb: 2, minHeight: 40, fontSize: '0.85rem' }}
                  >
                    {service.description}
                  </Typography>
                  <Chip
                    icon={<AccessTime sx={{ fontSize: 16 }} />}
                    label={`${service.duration} min`}
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(139, 195, 74, 0.12)',
                      color: '#558b2f',
                      fontWeight: 500,
                    }}
                  />
                </Box>
              </MotionPaper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Fade>
  );

  // Render date and time selection (Step 2)
  const renderDateTimeSelection = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#2c3e50' }}>
          Choose a Time
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Select your preferred date and time for your {selectedService?.name}
        </Typography>

        {/* Date Selection */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
          Select a Date
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            overflowX: 'auto',
            pb: 2,
            mb: 4,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#ddd', borderRadius: 3 },
          }}
        >
          {availableDates.map((date) => {
            const isSelected = selectedDate?.toDateString() === date.toDateString();
            const isToday = date.toDateString() === new Date().toDateString();
            return (
              <MotionBox
                key={date.toISOString()}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleDateSelect(date)}
                sx={{
                  minWidth: 72,
                  p: 1.5,
                  borderRadius: 2,
                  cursor: 'pointer',
                  textAlign: 'center',
                  backgroundColor: isSelected ? '#8bc34a' : 'white',
                  border: isSelected ? 'none' : '1px solid #e0e0e0',
                  color: isSelected ? 'white' : 'inherit',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#8bc34a',
                  },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    fontWeight: 500,
                    color: isSelected ? 'white' : 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                  }}
                >
                  {date.toLocaleDateString('en-AU', { weekday: 'short' })}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                  {date.getDate()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: isSelected ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                    fontSize: '0.7rem',
                  }}
                >
                  {date.toLocaleDateString('en-AU', { month: 'short' })}
                </Typography>
                {isToday && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: isSelected ? 'white' : '#8bc34a',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      mt: 0.5,
                    }}
                  >
                    Today
                  </Typography>
                )}
              </MotionBox>
            );
          })}
        </Box>

        {/* Time Selection */}
        {selectedDate && (
          <Fade in>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
                Available Times for {selectedDate.toLocaleDateString('en-AU', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>

              {timeSlots.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No appointments available on this day.
                </Alert>
              ) : (
                <Grid container spacing={3}>
                  {/* Morning */}
                  {groupedSlots.morning.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <WbSunny sx={{ color: '#f9a825', fontSize: 20 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Morning
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {groupedSlots.morning.slice(0, 6).map((slot) => (
                            <Chip
                              key={slot.time}
                              label={formatTime12(slot.time)}
                              onClick={() => setSelectedTime(slot.time)}
                              sx={{
                                fontWeight: 500,
                                backgroundColor: selectedTime === slot.time ? '#8bc34a' : 'white',
                                color: selectedTime === slot.time ? 'white' : 'inherit',
                                border: '1px solid',
                                borderColor: selectedTime === slot.time ? '#8bc34a' : '#e0e0e0',
                                '&:hover': {
                                  backgroundColor: selectedTime === slot.time ? '#7cb342' : '#f5f5f5',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Afternoon */}
                  {groupedSlots.afternoon.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <LightMode sx={{ color: '#ff9800', fontSize: 20 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Afternoon
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {groupedSlots.afternoon.slice(0, 6).map((slot) => (
                            <Chip
                              key={slot.time}
                              label={formatTime12(slot.time)}
                              onClick={() => setSelectedTime(slot.time)}
                              sx={{
                                fontWeight: 500,
                                backgroundColor: selectedTime === slot.time ? '#8bc34a' : 'white',
                                color: selectedTime === slot.time ? 'white' : 'inherit',
                                border: '1px solid',
                                borderColor: selectedTime === slot.time ? '#8bc34a' : '#e0e0e0',
                                '&:hover': {
                                  backgroundColor: selectedTime === slot.time ? '#7cb342' : '#f5f5f5',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Evening */}
                  {groupedSlots.evening.length > 0 && (
                    <Grid item xs={12} md={4}>
                      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#fafafa' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <NightsStay sx={{ color: '#5c6bc0', fontSize: 20 }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Evening
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {groupedSlots.evening.map((slot) => (
                            <Chip
                              key={slot.time}
                              label={formatTime12(slot.time)}
                              onClick={() => setSelectedTime(slot.time)}
                              sx={{
                                fontWeight: 500,
                                backgroundColor: selectedTime === slot.time ? '#8bc34a' : 'white',
                                color: selectedTime === slot.time ? 'white' : 'inherit',
                                border: '1px solid',
                                borderColor: selectedTime === slot.time ? '#8bc34a' : '#e0e0e0',
                                '&:hover': {
                                  backgroundColor: selectedTime === slot.time ? '#7cb342' : '#f5f5f5',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}

              {selectedTime && (
                <Alert
                  severity="success"
                  icon={<EventAvailable />}
                  sx={{ mt: 3, borderRadius: 2 }}
                >
                  Selected: {selectedDate.toLocaleDateString('en-AU', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })} at {formatTime12(selectedTime)}
                </Alert>
              )}
            </Box>
          </Fade>
        )}
      </Box>
    </Fade>
  );

  // Render patient details form (Step 3)
  const renderPatientDetails = () => (
    <Fade in timeout={400}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: '#2c3e50' }}>
          Your Details
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Please provide your contact information
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleFormChange('firstName', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleFormChange('lastName', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleFormChange('email', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleFormChange('phone', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.dateOfBirth}
                  onChange={(e) => handleFormChange('dateOfBirth', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reason for appointment (optional)"
                  multiline
                  rows={3}
                  placeholder="Please describe your symptoms or reason for visit..."
                  value={formData.appointmentReason}
                  onChange={(e) => handleFormChange('appointmentReason', e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Reminder Preferences
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.emailReminder}
                      onChange={(e) => handleFormChange('emailReminder', e.target.checked)}
                      sx={{ '&.Mui-checked': { color: '#8bc34a' } }}
                    />
                  }
                  label="Email reminder"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.smsReminder}
                      onChange={(e) => handleFormChange('smsReminder', e.target.checked)}
                      sx={{ '&.Mui-checked': { color: '#8bc34a' } }}
                    />
                  }
                  label="SMS reminder"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Booking Summary */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: '#fafafa',
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Booking Summary
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Schedule sx={{ color: '#8bc34a', mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedService?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedService?.duration} minutes
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <CalendarMonth sx={{ color: '#8bc34a', mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {selectedDate?.toLocaleDateString('en-AU', {
                        weekday: 'long',
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedTime && formatTime12(selectedTime)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <LocationOn sx={{ color: '#8bc34a', mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Health by Chiropractic
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      477 High Street, Melton VIC 3337
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Alert severity="info" sx={{ borderRadius: 2 }} icon={<Info />}>
                  <Typography variant="caption">
                    Payment will be collected at the clinic after your appointment.
                  </Typography>
                </Alert>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Fade>
  );

  // Render confirmation (Step 4)
  const renderConfirmation = () => (
    <Fade in timeout={400}>
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: 'rgba(139, 195, 74, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <CheckCircle sx={{ fontSize: 48, color: '#8bc34a' }} />
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: '#2c3e50' }}>
          Booking Confirmed
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Your appointment has been successfully booked
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: '#fafafa',
            border: '1px solid #e0e0e0',
            maxWidth: 450,
            mx: 'auto',
            mb: 4,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Confirmation Number
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, fontFamily: 'monospace' }}>
            {confirmationNumber}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={2} sx={{ textAlign: 'left' }}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Service</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedService?.name}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Duration</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedService?.duration} min</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Date</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedDate?.toLocaleDateString('en-AU', { weekday: 'short', month: 'short', day: 'numeric' })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Time</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedTime && formatTime12(selectedTime)}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Patient</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formData.firstName} {formData.lastName}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Alert severity="info" sx={{ maxWidth: 450, mx: 'auto', mb: 4, borderRadius: 2, textAlign: 'left' }}>
          A confirmation email has been sent to <strong>{formData.email}</strong>.
          {formData.smsReminder && ' You will also receive an SMS reminder before your appointment.'}
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => window.location.href = '/'}
            sx={{
              backgroundColor: '#8bc34a',
              '&:hover': { backgroundColor: '#7cb342' },
              borderRadius: 2,
              px: 4,
            }}
          >
            Return Home
          </Button>
          <Button
            variant="outlined"
            onClick={resetBooking}
            sx={{
              borderColor: '#8bc34a',
              color: '#8bc34a',
              '&:hover': { borderColor: '#7cb342', backgroundColor: 'rgba(139, 195, 74, 0.08)' },
              borderRadius: 2,
              px: 4,
            }}
          >
            Book Another
          </Button>
        </Box>
      </Box>
    </Fade>
  );

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 0: return renderPatientType();
      case 1: return renderServiceSelection();
      case 2: return renderDateTimeSelection();
      case 3: return renderPatientDetails();
      case 4: return renderConfirmation();
      default: return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        pt: { xs: 10, md: 12 },
        pb: 8,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#2c3e50',
              mb: 1,
            }}
          >
            Book an Appointment
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Health by Chiropractic • 477 High Street, Melton
          </Typography>
        </Box>

        {/* Progress Stepper */}
        {!bookingComplete && (
          <Stepper
            activeStep={step}
            alternativeLabel
            sx={{
              mb: 4,
              '& .MuiStepLabel-label': { fontWeight: 500 },
              '& .MuiStepIcon-root.Mui-active': { color: '#8bc34a' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#8bc34a' },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Main Content */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
            minHeight: 400,
          }}
        >
          <AnimatePresence mode="wait">
            {renderStepContent()}
          </AnimatePresence>

          {/* Navigation */}
          {!bookingComplete && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 5,
                pt: 3,
                borderTop: '1px solid #eee',
              }}
            >
              <Button
                onClick={handleBack}
                startIcon={<ArrowBack />}
                disabled={step === 0}
                sx={{ visibility: step === 0 ? 'hidden' : 'visible' }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceed() || isSubmitting}
                sx={{
                  backgroundColor: '#8bc34a',
                  '&:hover': { backgroundColor: '#7cb342' },
                  '&:disabled': { backgroundColor: '#e0e0e0' },
                  borderRadius: 2,
                  px: 4,
                  minWidth: 140,
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                ) : step === 3 ? (
                  'Confirm Booking'
                ) : (
                  'Continue'
                )}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default BookingSystem;
